import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ICompanyList } from '~/types/ranking'

import ClusterVariationCellRenderer from '~/components/CellRenderers/ClusterVariationCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { formatDecimalNumber } from '~/utils/number'
import { textColDef } from '~/utils/table'

const COLUMNS: Record<string, keyof ICompanyList> = {
  COMPANY_NAME: 'company_name',
  DOWNLOADS_D180_VS_PREVIOUS: 'downloads_d180_vs_previous',
  DOWNLOADS_D180_VS_PREVIOUS_APPS_IN_CLUSTERS: 'downloads_d180_vs_previous_apps_in_clusters',
  DOWNLOADS_D30: 'downloads_d30',
  DOWNLOADS_D30_APPS_IN_CLUSTERS: 'downloads_d30_apps_in_clusters',
  DOWNLOADS_D30_VS_PREVIOUS: 'downloads_d30_vs_previous',
  DOWNLOADS_D30_VS_PREVIOUS_APPS_IN_CLUSTERS: 'downloads_d30_vs_previous_apps_in_clusters',
  DOWNLOADS_D90_VS_PREVIOUS: 'downloads_d90_vs_previous',
  DOWNLOADS_D90_VS_PREVIOUS_APPS_IN_CLUSTERS: 'downloads_d90_vs_previous_apps_in_clusters',
  NB_APPS: 'nb_apps',
  NB_APPS_IN_CLUSTERS: 'nb_apps_in_clusters',
  NB_APPS_IN_TOP5_CLUSTER: 'nb_apps_in_top5_cluster',
  NB_CLUSTERS_IN: 'nb_clusters_in',
  REVENUE_D180_VS_PREVIOUS: 'revenue_d180_vs_previous',
  REVENUE_D180_VS_PREVIOUS_APPS_IN_CLUSTERS: 'revenue_d180_vs_previous_apps_in_clusters',
  REVENUE_D30: 'revenue_d30',
  REVENUE_D30_APPS_IN_CLUSTERS: 'revenue_d30_apps_in_clusters',
  REVENUE_D30_VS_PREVIOUS: 'revenue_d30_vs_previous',
  REVENUE_D30_VS_PREVIOUS_APPS_IN_CLUSTERS: 'revenue_d30_vs_previous_apps_in_clusters',
  REVENUE_D90_VS_PREVIOUS: 'revenue_d90_vs_previous',
  REVENUE_D90_VS_PREVIOUS_APPS_IN_CLUSTERS: 'revenue_d90_vs_previous_apps_in_clusters',
  SOV_REVENUE_D30_APPS_IN_CLUSTERS: 'sov_revenue_d30_apps_in_clusters',
}

const COLUMN_WIDTH = {
  MEDIUM: 200,
  SMALL: 130,
}

const numericColumnDefinition: ColDef = {
  cellClass: 'numeric-value',
  filter: 'agNumberColumnFilter',
  suppressSizeToFit: true,
  width: COLUMN_WIDTH.SMALL,
}

const columns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: COLUMNS.COMPANY_NAME,
    headerName: 'Company name',
    minWidth: 180,
    pinned: 'left',
    width: 180,
  }),
  {
    field: COLUMNS.NB_APPS_IN_TOP5_CLUSTER,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Leaders app', headerTitle: 'Number' }),
    },
    headerName: '',
    ...numericColumnDefinition,
  },
  {
    field: COLUMNS.NB_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'App in clusters', headerTitle: 'Number ' }),
    },
    headerName: '',
    ...numericColumnDefinition,
  },
  {
    field: COLUMNS.NB_CLUSTERS_IN,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Clusters', headerTitle: 'Number' }),
    },
    headerName: '',
    ...numericColumnDefinition,
  },
  {
    field: COLUMNS.NB_APPS,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Apps', headerTitle: 'Number ' }),
    },
    headerName: '',
    ...numericColumnDefinition,
  },
  {
    field: COLUMNS.SOV_REVENUE_D30_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Last 30 days',
        headerTitle: 'SOV Revenue in clusters',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueFormatter: ({ value }: ValueFormatterParams): string => (value ? `${formatDecimalNumber(value * 100)}%` : ''),
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D30_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'In clusters 30 days',
        headerTitle: 'Revenue',
      }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams) => (value ? `${formatDecimalNumber(value)}$` : ''),
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_D30_VS_PREVIOUS_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 30 Days',
        headerTitle: 'Revenue in clusters VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.revenue_d30_vs_previous_apps_in_clusters ? data.revenue_d30_vs_previous_apps_in_clusters * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_D90_VS_PREVIOUS_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 90 Days',
        headerTitle: 'Revenue in clusters VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.revenue_d90_vs_previous_apps_in_clusters ? data.revenue_d90_vs_previous_apps_in_clusters * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_D180_VS_PREVIOUS_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 180 Days',
        headerTitle: 'Revenue in clusters VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.revenue_d180_vs_previous_apps_in_clusters ? data.revenue_d180_vs_previous_apps_in_clusters * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    field: COLUMNS.DOWNLOADS_D30_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Last 30 days',
        headerTitle: 'Downloads in clusters',
      }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams): string => (value ? `${formatDecimalNumber(value)}` : ''),
    ...numericColumnDefinition,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_D30_VS_PREVIOUS_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 30 Days',
        headerTitle: 'Downloads in clusters VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.downloads_d30_vs_previous_apps_in_clusters
        ? data.downloads_d30_vs_previous_apps_in_clusters * 100
        : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_D90_VS_PREVIOUS_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 90 Days',
        headerTitle: 'Downloads in clusters VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.downloads_d90_vs_previous_apps_in_clusters
        ? data.downloads_d90_vs_previous_apps_in_clusters * 100
        : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_D180_VS_PREVIOUS_APPS_IN_CLUSTERS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 180 Days',
        headerTitle: 'Downloads in clusters VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.downloads_d180_vs_previous_apps_in_clusters
        ? data.downloads_d180_vs_previous_apps_in_clusters * 100
        : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Last 30 days',
        headerTitle: 'Revenue',
      }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams) => (value ? `${formatDecimalNumber(value)}$` : ''),
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_D30_VS_PREVIOUS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 30 Days',
        headerTitle: 'Revenue VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.revenue_d30_vs_previous ? data.revenue_d30_vs_previous * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_D90_VS_PREVIOUS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 90 Days',
        headerTitle: 'Revenue VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.revenue_d90_vs_previous ? data.revenue_d90_vs_previous * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_D180_VS_PREVIOUS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 180 Days',
        headerTitle: 'Revenue VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.revenue_d180_vs_previous ? data.revenue_d180_vs_previous * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    field: COLUMNS.DOWNLOADS_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Last 30 days',
        headerTitle: 'Downloads',
      }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams): string => (value ? `${formatDecimalNumber(value)}` : ''),
    ...numericColumnDefinition,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_D30_VS_PREVIOUS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 30 Days',
        headerTitle: 'Downloads VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.downloads_d30_vs_previous ? data.downloads_d30_vs_previous * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_D90_VS_PREVIOUS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 90 Days',
        headerTitle: 'Downloads VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.downloads_d90_vs_previous ? data.downloads_d90_vs_previous * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
  {
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_D180_VS_PREVIOUS,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        headerSubtitle: 'Previous period 180 Days',
        headerTitle: 'Downloads VS',
      }),
    },
    headerName: '',
    sortable: true,
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICompanyList>) =>
      data && data.downloads_d180_vs_previous ? data.downloads_d180_vs_previous * 100 : 0,
    width: COLUMN_WIDTH.MEDIUM,
  },
]

export default columns
